import { Button, Card, CardActionArea, CardContent, Grid, Typography, Box, SxProps } from '@mui/material';
import { AccumulationIcon, PriceMemberIcon, RedemptionWhiteIcon, WeeklyDrawIcon } from 'assets/icons';
import FamiliplusLongVersion from '../../images/FamiliplusLongVersion.png';
import DiagonalLineImg from '../../images/diagonal-line.png';
import DiamondShapesImg from '../../images/diamond-shapes.png';
import TwoCubesImg from '../../images/2-cubes.png';
import DotsImg from '../../images/dots.png';
import { Body } from 'components/Typography';
import { useTranslation } from 'react-i18next';
import config from '../../config';
import i18n from 'i18n';
import { useNavigate } from 'common/hooks/useNavigate';

const TRANSLATION_KEY = 'NotMember';

interface BoxItem {
  icon: JSX.Element;
  title: string;
  text: string;
}

export default function FamiliplusNotMember(): JSX.Element {
  const { t } = useTranslation('familiplusPage');
  const navigate = useNavigate();

  const getAvantagesFamiliplusUrl = (): string => {
    if (i18n.resolvedLanguage == 'en') {
      return config.AVANTAGES_FAMILIPLUS_EN_URL;
    } else {
      return config.AVANTAGES_FAMILIPLUS_FR_URL;
    }
  };

  const items: BoxItem[] = [
    {
      icon: <WeeklyDrawIcon {...iconStyle} />,
      title: t(`${TRANSLATION_KEY}.earnPointsWeekly`),
      text: t(`${TRANSLATION_KEY}.weeklyDraw`),
    },
    {
      icon: <PriceMemberIcon {...iconStyle} />,
      title: t(`${TRANSLATION_KEY}.saveWithMemberPrice`),
      text: t(`${TRANSLATION_KEY}.getCompetitivePrice`),
    },
    {
      icon: <AccumulationIcon {...iconStyle} />,
      title: t(`${TRANSLATION_KEY}.accumulatePointsQuickly`),
      text: t(`${TRANSLATION_KEY}.accumulatePointsEverywhere`),
    },
  ];

  return (
    <>
      <Box id="becomeMemberBannerBox" sx={bannerSx}>
        <img src={FamiliplusLongVersion} width={'170px'} alt="Familiplus" />
        <Typography variant="body1" sx={{ mt: 1, color: (theme) => theme.palette.primary.main }}>
          {t(`${TRANSLATION_KEY}.becomeMemberAndBenefit`)}
        </Typography>
        <Button
          variant="contained"
          size="large"
          color="error"
          sx={{ mt: 2, boxShadow: 'none' }}
          onClick={() =>
            navigate(
              `/${i18n.resolvedLanguage}/${i18n.resolvedLanguage == 'fr' ? 'Compte' : 'Account'}/Familiplus/${
                i18n.resolvedLanguage == 'fr' ? 'Carte' : 'Card'
              }`
            )
          }>
          {t(`${TRANSLATION_KEY}.becomeMember`)}
        </Button>
      </Box>

      <Card id="becomeMemberCard" sx={{ borderRadius: '6px', padding: '24px' }}>
        <Typography sx={{ fontSize: '1.5rem', color: (theme) => theme.palette.error.main }} variant="h1">
          <img src={FamiliplusLongVersion} width={'132px'} alt="Familiplus" />
          <Box sx={{ mt: '3px' }}>{t(`${TRANSLATION_KEY}.bigPlus`)}</Box>
        </Typography>
        <Typography variant="body1" sx={{ mt: '10px', color: (theme) => theme.palette.primary.main }}>
          {t(`${TRANSLATION_KEY}.accumulatePointsAndStartEarning`)}
        </Typography>

        <Grid container spacing={2} sx={{ mt: '8px' }}>
          {items.map((item, index) => (
            <BoxItem key={`FamiliplusAdvantage${index}`} {...item} />
          ))}
          <Grid id="seeAdvantagesCard" item xs={12} sm={6} md={3}>
            <Card sx={allAdvantagesSx}>
              <CardActionArea onClick={() => window.open(getAvantagesFamiliplusUrl())}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <RedemptionWhiteIcon {...iconStyle} />
                  <Typography variant="h3" sx={{ mt: 1, color: '#fff' }}>
                    {t(`${TRANSLATION_KEY}.seeAllBenefits`)}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>
      </Card>
    </>
  );
}

function BoxItem(props: BoxItem): JSX.Element {
  const { icon, title, text } = props;

  return (
    <Grid item xs={12} sm={6} md={3}>
      <Card sx={{ borderRadius: '6px', border: '1px solid #bec6cd', height: '100%' }} elevation={0}>
        <CardContent>
          {icon}
          <Typography variant="h3" sx={{ mt: 1 }}>
            {title}
          </Typography>
          <Body mt={2} sx={{ color: (theme) => theme.palette.primary.main, lineHeight: 1.6, letterSpacing: '0.4px' }}>
            {text}
          </Body>
        </CardContent>
      </Card>
    </Grid>
  );
}

const iconStyle = {
  width: '56px',
  height: '56px',
};

const bannerSx: SxProps = {
  background: `url(${DiagonalLineImg}) no-repeat,
   url(${DiamondShapesImg}) no-repeat,
   url(${DotsImg}) no-repeat,
   url(${TwoCubesImg}) no-repeat,           
   radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, #EDEDED 100%)`,
  backgroundSize: {
    xs: '120px, 70px, 40px, 80px',
    sm: 'auto, auto, auto, auto',
  },
  backgroundPosition: {
    xs: '-48px 5px, -20px 90%, 87% top, 101% 85%',
    sm: '9% top, 6% bottom, 82% top, 93% 40px',
  },
  borderRadius: '6px',
  mb: '24px',
  padding: '32px',
  textAlign: 'center',
};

const allAdvantagesSx: SxProps = {
  display: 'flex',
  height: '100%',
  background:
    'linear-gradient(180deg, #EE172E 0%, #CE0E19 98.75%), linear-gradient(0deg, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0.08))',
  border: '2px solid rgba(0, 0, 0, 0.08)',
  borderRadius: '6px',
};
